import { ChevronRight } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/shared/components/EmptyState";
import { MarketplaceChip } from "@/shared/components/MarketplaceChip";
import { Badge } from "@/shared/ui/badge";

import { formatBangkokDateTime, money } from "../lib/format";
import { returnIntent, returnStatusLabel } from "../lib/status";
import type { ReturnRec } from "../lib/types";

interface ReturnsTableProps {
  returns: ReturnRec[];
  onOpen: (returnId: string) => void;
}

export function ReturnsTable({ returns, onOpen }: ReturnsTableProps): JSX.Element {
  const { t } = useTranslation("orders");

  if (returns.length === 0) {
    return (
      <EmptyState
        title={t("empty_no_returns")}
        body={t("empty_no_returns_desc")}
        data-testid="returns-empty"
      />
    );
  }

  return (
    <div
      className="overflow-x-auto rounded-[14px] border border-[var(--premium-border)] shadow-[var(--sh-card)]"
      data-testid="returns-table"
    >
      <table className="min-w-full border-collapse">
        <thead className="bg-white border-b border-[var(--row-divider)]">
          <tr>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_order")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_return_id")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_marketplace")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_reason")}
            </th>
            <th className="h-[52px] px-4 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_items")}
            </th>
            <th className="h-[52px] px-4 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_refund")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_return_status")}
            </th>
            <th className="h-[52px] px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
              {t("th_updated")}
            </th>
            <th className="h-[52px] px-4">
              <span className="sr-only">{t("th_actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--row-divider)]">
          {returns.map((r) => (
            <tr
              key={r.ret}
              data-testid="return-row"
              className="group cursor-pointer transition-colors hover:bg-[var(--row-hover)]"
              onClick={() => onOpen(r.ret)}
            >
              {/* Order ID */}
              <td className="h-[52px] px-4 text-sm text-[var(--text-primary)] whitespace-nowrap">
                <span className="font-mono text-[12px] font-semibold">{r.order}</span>
              </td>

              {/* Return ID */}
              <td className="h-[52px] px-4 text-sm text-[var(--text-primary)] whitespace-nowrap">
                <span className="font-mono text-[12px] font-semibold">{r.ret}</span>
              </td>

              {/* Marketplace */}
              <td className="h-[52px] px-4 whitespace-nowrap">
                <MarketplaceChip marketplace={r.mkp} />
              </td>

              {/* Reason */}
              <td
                className="h-[52px] max-w-[360px] truncate px-4 text-sm text-[var(--text-secondary)]"
                title={r.reason}
              >
                {r.reason}
              </td>

              {/* Items count (sum of q) */}
              <td className="h-[52px] px-4 text-right text-sm tabular-nums text-[var(--text-primary)] whitespace-nowrap">
                {r.items.reduce((s, i) => s + i.q, 0)}
              </td>

              {/* Refund */}
              <td className="h-[52px] px-4 text-right text-sm tabular-nums font-semibold text-[var(--text-primary)] whitespace-nowrap">
                {money(r.refund)}
              </td>

              {/* Return status badge */}
              <td className="h-[52px] px-4 whitespace-nowrap">
                <Badge intent={returnIntent(r.status)} dot>
                  {returnStatusLabel(r.status, t)}
                </Badge>
              </td>

              {/* Updated */}
              <td className="h-[52px] px-4 text-sm tabular-nums text-[var(--text-subtle)] whitespace-nowrap">
                {formatBangkokDateTime(r.updated)}
              </td>

              {/* Action */}
              <td className="h-[52px] px-4 whitespace-nowrap">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(r.ret);
                  }}
                  aria-label={t("act_view")}
                  className="grid h-8 w-8 place-items-center rounded-lg text-[var(--text-subtle)] hover:bg-white hover:text-[var(--text-primary)] hover:shadow-[var(--sh-xs)] hover:border hover:border-[var(--premium-border-strong)] focus-ring"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
